import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { SelectOption } from '../../../../components/form/select-field/select-field.config';
import { RiskActionProposesComponent } from '../risk-action-proposes/risk-action-proposes.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { OptionService } from '../../../../services/api/option.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';

import {
  Risk,
  RiskActionPropose,
  RiskActionProposePayload,
  RiskPayload,
  riskUserFullName
} from '../../../../models/entities/risk';

const EMPTY_OPTION: SelectOption = { key: '', value: '-- select' };

@Component({
  selector: 'risk-form',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent,
    TextAreaFieldComponent,
    RiskActionProposesComponent
  ],
  templateUrl: './risk-form.component.html',
  styleUrl: './risk-form.component.scss'
})
export class RiskFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() risk?: Risk;
  @Input() riskId?: number;
  @Input() isEditPage: boolean = false;
  @Input() readonlyForm: boolean = false;

  @Output() onSavedData = new EventEmitter<Risk>();

  form: FormGroup;
  typeOptions: SelectOption[] = [EMPTY_OPTION];
  statusOptions: SelectOption[] = [EMPTY_OPTION];
  priorityOptions: SelectOption[] = [EMPTY_OPTION];
  responsibleOptions: SelectOption[] = [EMPTY_OPTION];
  timeFrameOptions: SelectOption[] = [EMPTY_OPTION];

  currentUser: any = null;
  canModify: boolean = false;

  private readonly LOADER_CONTEXT = 'risk-form';
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private qualityManagementService: QualityManagementService,
    private optionService: OptionService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.form = this.formBuilder.group({
      code: [{ value: '', disabled: true }],
      creator: [{ value: '', disabled: true }],
      type: ['', [Validators.required]],
      date: ['', [Validators.required]],
      description: ['', [Validators.required]],
      status: ['', [Validators.required]],
      nextReview: ['', [Validators.required]],
      opportunity: ['', [Validators.required]],
      threat: ['', [Validators.required]],
      priority: ['', [Validators.required]],
      actionProposes: this.formBuilder.array<FormGroup>([])
    });
  }

  ngOnInit(): void {
    this.mainLayoutService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        this.patchReadonlyFields();
      });

    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_RISKS',
          'modify'
        );
        this.applyFieldPermissions();
      });

    this.loadOptions();
    this.applyRisk();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['risk'] || changes['readonlyForm']) {
      this.applyRisk();
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get actionProposes(): FormArray<FormGroup> {
    return this.form.get('actionProposes') as FormArray<FormGroup>;
  }

  get canSave(): boolean {
    return this.canModify && !this.readonlyForm;
  }

  get code(): string {
    return this.risk?.code ?? '';
  }

  get creatorLabel(): string {
    if (this.risk?.creatorUser) {
      return riskUserFullName(this.risk.creatorUser);
    }
    return this.currentUser
      ? `${this.currentUser.lastName ?? ''} ${this.currentUser.firstName ?? ''}`.trim()
      : '';
  }

  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  addActionPropose(): void {
    this.actionProposes.push(this.buildActionProposeGroup());
  }

  removeActionPropose(index: number): void {
    this.actionProposes.removeAt(index);
  }

  reloadRisk(): void {
    if (!this.riskId) {
      return;
    }

    this.qualityManagementService.detailRisk(this.riskId).subscribe({
      next: (response: any) => {
        const risk: Risk | null = this.qualityManagementService.readResponseKey(response, 'risk') ?? null;

        if (risk) {
          this.risk = risk;
          this.applyRisk();
        }
      },
      error: (error: any) => {
        console.error('Error reloading the risk:', error);
      }
    });
  }

  submitForm(): void {
    if (!this.canSave) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertNotificationService.error('Please fill in all the required fields.');
      return;
    }

    const payload = this.buildPayload();

    const request$ = this.riskId
      ? this.qualityManagementService.updateRisk(this.riskId, payload)
      : this.qualityManagementService.createRisk(payload);

    this.loaderService.startLoading(this.LOADER_CONTEXT);

    request$.subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        const risk: Risk | null = this.qualityManagementService.readResponseKey(response, 'risk') ?? null;

        if (!risk) {
          this.applyBackendErrors(response);
          return;
        }

        this.risk = risk;
        this.riskId = risk.riskId ?? this.riskId;
        this.applyRisk();
        this.alertNotificationService.success('Risk saved.');
        this.onSavedData.emit(risk);
      },
      error: (error: any) => {
        console.error('Error saving the risk:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.applyBackendErrors(error?.error, error);
      }
    });
  }

  private buildActionProposeGroup(actionPropose?: RiskActionPropose): FormGroup {
    return this.formBuilder.group({
      riskActionProposeId: [actionPropose?.riskActionProposeId ?? null],
      actionPropose: [actionPropose?.actionPropose ?? '', [Validators.required]],
      responsibleUserId: [actionPropose?.responsibleUser?.userId ?? '', [Validators.required]],
      riskTimeFrameId: [actionPropose?.riskTimeFrame?.riskTimeFrameId ?? ''],
      lastEvaluation: [
        this.qualityManagementService.toDateTimeLocal(actionPropose?.lastEvaluation),
        [Validators.required]
      ],
      nextReview: [
        this.qualityManagementService.toDateTimeLocal(actionPropose?.nextReview),
        [Validators.required]
      ],
      comment: [actionPropose?.comment ?? '', [Validators.required]],
      attachments: [actionPropose?.attachments ?? []]
    });
  }

  private buildPayload(): RiskPayload {
    const value = this.form.getRawValue();

    const actionProposes: RiskActionProposePayload[] = (value.actionProposes ?? []).map((row: any) => ({
      riskActionProposeId: row.riskActionProposeId ?? null,
      actionPropose: row.actionPropose,
      responsibleUserId: Number(row.responsibleUserId),
      riskTimeFrameId: this.toNullableId(row.riskTimeFrameId),
      lastEvaluation: this.qualityManagementService.fromDateTimeLocal(row.lastEvaluation),
      nextReview: this.qualityManagementService.fromDateTimeLocal(row.nextReview),
      comment: row.comment
    }));

    return {
      type: value.type,
      date: this.qualityManagementService.fromDateTimeLocal(value.date),
      description: value.description,
      status: value.status,
      nextReview: this.qualityManagementService.fromDateTimeLocal(value.nextReview),
      opportunity: value.opportunity,
      threat: value.threat,
      priority: value.priority,
      actionProposes
    };
  }

  private toNullableId(value: any): number | null {
    return value === '' || value === null || value === undefined ? null : Number(value);
  }

  private loadOptions(): void {
    this.loadOptionList(this.optionService.riskTypes(), 'typeOptions', 'the risk types list');
    this.loadOptionList(this.optionService.riskStatuses(), 'statusOptions', 'the risk statuses list');
    this.loadOptionList(this.optionService.riskPriorities(), 'priorityOptions', 'the risk priorities list');
    this.loadOptionList(this.optionService.riskResponsibles(), 'responsibleOptions', 'the users list');
    this.loadOptionList(this.optionService.riskTimeFrames(), 'timeFrameOptions', 'the time frames list');
  }

  private loadOptionList(source$: any, target: string, label: string): void {
    source$.subscribe({
      next: (response: any) => {
        (this as any)[target] = [EMPTY_OPTION, ...(response?.data?.options ?? [])];
      },
      error: (error: any) => {
        console.error(`Error loading ${label}:`, error);
        this.alertNotificationService.error(`Failed to load ${label}.`);
      }
    });
  }

  private applyRisk(): void {
    if (this.risk) {
      this.riskId = this.risk.riskId ?? this.riskId;

      this.form.patchValue({
        type: this.risk.type ?? '',
        date: this.qualityManagementService.toDateTimeLocal(this.risk.date),
        description: this.risk.description ?? '',
        status: this.risk.status ?? '',
        nextReview: this.qualityManagementService.toDateTimeLocal(this.risk.nextReview),
        opportunity: this.risk.opportunity ?? '',
        threat: this.risk.threat ?? '',
        priority: this.risk.priority ?? ''
      }, { emitEvent: false });

      this.actionProposes.clear({ emitEvent: false });
      (this.risk.actionProposes ?? []).forEach(actionPropose => {
        this.actionProposes.push(this.buildActionProposeGroup(actionPropose), { emitEvent: false });
      });
    }

    this.patchReadonlyFields();
    this.applyFieldPermissions();
  }

  private patchReadonlyFields(): void {
    this.form.patchValue({
      code: this.code,
      creator: this.creatorLabel
    }, { emitEvent: false });
  }

  private applyFieldPermissions(): void {
    if (!this.canSave) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
    this.form.get('code')?.disable({ emitEvent: false });
    this.form.get('creator')?.disable({ emitEvent: false });
  }

  private applyBackendErrors(response: any, error?: any): void {
    const validationErrors = response?.error?.validationErrors
      ?? response?.validationErrors
      ?? response?.errors
      ?? null;

    const messagesByField = this.normalizeValidationErrors(validationErrors);

    if (messagesByField) {
      Object.keys(messagesByField).forEach(field => {
        const control = this.resolveControl(field);

        if (control) {
          control.markAsTouched();
          control.setErrors({
            ...(control.errors ?? {}),
            backendError: messagesByField[field],
            back: messagesByField[field]
          });
        }
      });

      this.alertNotificationService.error(Object.values(messagesByField).join(' '));
      return;
    }

    this.alertNotificationService.error(
      this.qualityManagementService.riskErrorMessage(
        error ?? { error: response },
        'Failed to save the risk.'
      )
    );
  }

  private normalizeValidationErrors(validationErrors: any): Record<string, string> | null {
    if (Array.isArray(validationErrors)) {
      const messagesByField: Record<string, string> = {};

      validationErrors
        .filter(entry => entry?.field != null)
        .forEach(entry => messagesByField[entry.field] = entry.defaultMessage);

      return Object.keys(messagesByField).length ? messagesByField : null;
    }

    if (validationErrors && typeof validationErrors === 'object') {
      return validationErrors as Record<string, string>;
    }

    return null;
  }

  private resolveControl(field: string): FormControl | null {
    const path = field.replace(/\[(\d+)\]/g, '.$1');
    return (this.form.get(path) as FormControl) ?? null;
  }
}
